import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, EMPTY } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseService } from '../../../../_services/base/base.service';
import { SessionUserHashManagerService } from '../../../../_services/session-user-hash-manager/session-user-hash-manager.service';
import { environment } from '../../../../../environments/environment';

/** A stored evidence file as the API lists it: its name and its link, api/Evidence/{id}. */
export interface StoredEvidenceFile {
  name: string;
  url?: string;
}

/**
 * Opens stored evidence of work (session report, progress report, ILP and their timeline cards). The API hands
 * out api/Evidence/{id} links rather than blob URLs: the reports storage account takes no public traffic, so a
 * browser cannot open a blob link of any kind. The file is fetched through the API with the signed-in user's
 * headers, which a plain link cannot send, and saved under the name it was uploaded with. A failure is shown
 * by the error interceptor, like any other API error.
 */
@Injectable({ providedIn: 'root' })
export class LeapV2EvidenceService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(
    private http: HttpClient,
    protected hashManagerService: SessionUserHashManagerService
  ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  /**
   * Fetches one stored file's content. loadingPage: the page is loading the file in its URL and shows its own
   * not-found state, so the support modal stays out (40973).
   */
  download(file: StoredEvidenceFile, loadingPage: boolean = false): Observable<Blob> {
    const base = loadingPage ? this.prepareBookingLoadOptions(null) : this.prepareOptions(null);
    const options = { ...base, responseType: 'blob' as 'blob' };
    return this.http.get(this.apiUrl + file.url, options).pipe(
      map((data: any) => data.body as Blob)
    );
  }

  /** Downloads a stored file and hands it to the browser to open or save. */
  open(file: StoredEvidenceFile): void {
    if (!file?.url) return;
    this.download(file).pipe(catchError(() => EMPTY)).subscribe(blob => saveBlob(blob, file.name));
  }
}

/** Hands a downloaded file to the browser under its own name, as the client report export does. */
function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name || 'evidence';
  link.click();
  URL.revokeObjectURL(url);
}
